import type { Coverage } from '@/services/coverages/types';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  useBusinessActivities,
  useBusinessCoverages,
  useBusinessPlanResourcesByCode,
  useFireProtectionRatingTypes,
  useTheftProtectionRatingTypes,
} from '../../quotes/hooks/business';
import { useConstructionTypes } from '../../quotes/hooks/homeowner/use-construction-types.hook';
import { useInsuredObjects } from '../../quotes/hooks/homeowner/use-insured-objects.hook';
import {
  type BusinessLocation,
  formatBusinessLocationSummary,
} from '../../quotes/components/quote-steps/business-location';

type BusinessLocationViewCardProps = {
  location: BusinessLocation;
  locationNumber: number;
  isOpen: boolean;
  onToggleLocation: () => void;
  coveragesCatalog?: Coverage[];
};

const QUESTIONNAIRE_LABELS = [
  'Indique os sistemas protecionais contra incêndio.',
  'O local de risco encontra-se em piso superior?',
  'O imóvel está localizado dentro de shopping?',
  'Indique os sistemas protecionais contra roubo.',
];

const STATE_LABELS: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins',
};

/** Campo de leitura estilizado como input desabilitado. */
function FieldDisplay({
  label,
  value,
  isSelect = false,
}: {
  label: string;
  value?: string | null;
  isSelect?: boolean;
}) {
  return (
    <div className='flex flex-col gap-1.5'>
      <label className='text-sm font-bold text-alba-black-500'>{label}</label>
      <div className='relative flex h-10 items-center rounded-xl border border-alba-gray-300 bg-alba-gray-100 px-4 text-sm text-alba-gray-600'>
        <span className='truncate'>{value || ''}</span>
        {isSelect && (
          <ChevronDown
            className='absolute right-3 h-4 w-4 text-alba-gray-500'
            strokeWidth={1.6}
          />
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className='space-y-4'>
      <h3 className='text-base font-medium text-alba-black-500'>{title}</h3>
      {children}
    </div>
  );
}

export function BusinessLocationViewCard({
  location,
  locationNumber,
  isOpen,
  onToggleLocation,
  coveragesCatalog,
}: BusinessLocationViewCardProps) {
  const [assistanceDetailsOpen, setAssistanceDetailsOpen] = useState(false);

  // Catálogos pra resolver os códigos -> labels quando location não traz pronto.
  const { data: ownCatalog = [] } = useBusinessCoverages(
    coveragesCatalog ? undefined : location.activityType
  );
  const resolvedCatalog = coveragesCatalog ?? ownCatalog;

  const { data: constructionTypesData } = useConstructionTypes();
  const { data: insuredObjectTypesData } = useInsuredObjects();
  const { data: businessActivitiesData } = useBusinessActivities();
  const { data: fireProtectionRatingTypes } = useFireProtectionRatingTypes();
  const { data: theftProtectionRatingTypes } = useTheftProtectionRatingTypes();
  const { data: planResources = [] } = useBusinessPlanResourcesByCode(
    location.assistancePackageCode
  );

  const resolvedActivityTypeLabel =
    (businessActivitiesData ?? []).find(
      a => String(a.code) === location.activityType
    )?.description ?? location.activityTypeLabel;

  const resolvedConstructionTypeLabel =
    (constructionTypesData ?? []).find(
      t => String(t.code) === location.constructionType
    )?.description ?? location.constructionTypeLabel;

  const resolvedInsuredObjectTypeLabel =
    (insuredObjectTypesData ?? []).find(
      t => String(t.code) === location.insuredObjectType
    )?.description ?? location.insuredObjectTypeLabel;

  const coverageItems = (location.coveragesPayload ?? [])
    .filter(c => c.value > 0 && !['ASSIST', 'CAPITA'].includes(c.code))
    .map(c => {
      const catalogEntry = resolvedCatalog?.find(cat => cat.code === c.code);
      return {
        name: c.displayName ?? catalogEntry?.name ?? c.code,
        value: c.value.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }),
        required: catalogEntry?.required ?? false,
      };
    });
  const basicCoverages = coverageItems.filter(c => c.required);
  const optionalCoverages = coverageItems.filter(c => !c.required);

  const contractedAssistances = (location.benefitsPayload ?? [])
    .filter(b => {
      const code = String(b.code || '').trim().toUpperCase();
      if (!code) return false;
      if (code === 'CAPITA' || code.endsWith('CAP')) return false;
      return true;
    })
    .map(b => {
      // Prefere o nome que veio direto na resposta (productElementDesc),
      // cai pro catálogo de planResources, e por fim pro código bruto.
      const resource = planResources.find(
        r => String(r.code || '').trim() === String(b.code).trim()
      );
      return {
        code: b.code,
        name: b.displayName?.trim() || resource?.name?.trim() || b.code,
        value: (b.value ?? 0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }),
      };
    });

  const QUESTION_KIND: ('fire' | 'yesno' | 'theft')[] = [
    'fire',
    'yesno',
    'yesno',
    'theft',
  ];

  const resolveQuestionnaireDisplay = (
    index: number,
    answer: string | string[] | undefined
  ): string => {
    const kind = QUESTION_KIND[index];
    const resolveCode = (code: string): string => {
      if (kind === 'yesno') {
        if (code === 'Y' || code === 'S') return 'Sim';
        if (code === 'N') return 'Não';
        return code;
      }
      const catalog =
        kind === 'fire'
          ? (fireProtectionRatingTypes ?? [])
          : (theftProtectionRatingTypes ?? []);
      const found = catalog.find(item => String(item.code) === code);
      return found?.description || code;
    };
    if (Array.isArray(answer)) {
      return answer.length > 0 ? answer.map(resolveCode).join(', ') : '-';
    }
    return answer ? resolveCode(answer) : '-';
  };

  const stateLabel = STATE_LABELS[location.state] || location.state;

  return (
    <div
      key={location.id}
      className='rounded-xl border border-alba-gray-400 p-4'
    >
      <div className='flex items-start justify-between gap-4'>
        <div className='space-y-1'>
          <p className='text-xl font-bold text-alba-black-500'>
            {`Local ${String(locationNumber).padStart(2, '0')}.`}
          </p>
          <p className='text-sm font-bold text-alba-black-500'>
            {formatBusinessLocationSummary(location)}
          </p>
        </div>
        <button
          type='button'
          onClick={onToggleLocation}
          className='rounded-lg p-2 text-alba-blue-500 transition hover:bg-alba-gray-200'
        >
          {isOpen ? (
            <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
          ) : (
            <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
          )}
        </button>
      </div>

      {isOpen && (
        <div className='mt-4 space-y-6 border-t border-alba-gray-300 pt-4'>
          <Section title='Dados do Risco'>
            <FieldDisplay label='CEP' value={location.cep} />
            <FieldDisplay label='Endereço' value={location.street} />
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <FieldDisplay label='Número' value={location.number} />
              <FieldDisplay label='Complemento' value={location.complement} />
            </div>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
              <FieldDisplay label='Bairro' value={location.neighborhood} />
              <FieldDisplay label='Cidade' value={location.city} />
              <FieldDisplay label='Estado' value={stateLabel} isSelect />
            </div>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <FieldDisplay
                label='Tipo de Atividade Realizada'
                value={resolvedActivityTypeLabel}
                isSelect
              />
              <FieldDisplay
                label='Tipo de Construção'
                value={resolvedConstructionTypeLabel}
                isSelect
              />
            </div>
            <label className='flex items-center gap-2 text-sm text-alba-black-500'>
              <input
                type='checkbox'
                checked={location.isLandmarked}
                disabled
                className='h-4 w-4 rounded border-alba-gray-400 accent-alba-blue-500'
              />
              <span>Imóvel Tombado?</span>
            </label>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <FieldDisplay
                label='Objeto Segurado'
                value={resolvedInsuredObjectTypeLabel}
                isSelect
              />
              <FieldDisplay
                label='Valor em Risco'
                value={location.riskValue}
              />
            </div>
          </Section>

          <Section title='Coberturas'>
            <div className='space-y-4'>
              {basicCoverages.length > 0 && (
                <div className='rounded-xl border border-alba-gray-400 p-4'>
                  <div className='border-b border-alba-gray-300 pb-2'>
                    <p className='text-sm font-bold text-alba-black-500'>
                      Básicas
                    </p>
                  </div>
                  <div className='space-y-2 pt-2'>
                    {basicCoverages.map(item => (
                      <div
                        key={item.name}
                        className='flex items-start justify-between gap-4 text-xs text-alba-gray-600'
                      >
                        <span>{item.name}</span>
                        <span className='text-right'>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {optionalCoverages.length > 0 && (
                <div className='rounded-xl border border-alba-gray-400 p-4'>
                  <div className='border-b border-alba-gray-300 pb-2'>
                    <p className='text-sm font-bold text-alba-black-500'>
                      Opcionais
                    </p>
                  </div>
                  <div className='space-y-2 pt-2'>
                    {optionalCoverages.map(item => (
                      <div
                        key={item.name}
                        className='flex items-start justify-between gap-4 text-xs text-alba-gray-600'
                      >
                        <span>{item.name}</span>
                        <span className='text-right'>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {coverageItems.length === 0 && (
                <p className='text-sm text-alba-gray-500'>
                  Nenhuma cobertura selecionada.
                </p>
              )}
            </div>
          </Section>

          <Section title='Assistências/Sorteio'>
            <div className='rounded-xl border border-alba-gray-400 px-4 py-3'>
              <div className='flex items-start justify-between gap-4'>
                <p className='text-sm font-bold text-alba-black-500'>
                  {location.assistancePackage
                    ? `Pacote: ${location.assistancePackage}`
                    : 'Sem pacote selecionado'}
                </p>
                <div className='flex flex-col items-end gap-1'>
                  <p className='text-sm font-normal text-alba-gray-600'>
                    R$ 0,00
                  </p>
                  {contractedAssistances.length > 0 && (
                    <button
                      type='button'
                      onClick={() => setAssistanceDetailsOpen(prev => !prev)}
                      className='flex items-center gap-1 text-xs text-alba-gray-600'
                    >
                      <span>Mais Detalhes</span>
                      {assistanceDetailsOpen ? (
                        <ChevronUp className='h-3 w-3' strokeWidth={1.6} />
                      ) : (
                        <ChevronDown className='h-3 w-3' strokeWidth={1.6} />
                      )}
                    </button>
                  )}
                </div>
              </div>
              {assistanceDetailsOpen && contractedAssistances.length > 0 && (
                <div className='mt-3 space-y-1 border-t border-alba-gray-300 pt-3'>
                  {contractedAssistances.map(a => (
                    <div
                      key={a.code}
                      className='flex items-start justify-between gap-4 text-xs text-alba-gray-600'
                    >
                      <span>{a.name}</span>
                      <span className='text-right'>{a.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Section>

          <Section title='Questionário'>
            <div className='space-y-3'>
              {QUESTIONNAIRE_LABELS.map((question, index) => (
                <div
                  key={question}
                  className='flex items-center gap-6'
                >
                  <label className='flex-1 text-sm text-alba-black-500'>
                    {question}
                  </label>
                  <div className='relative flex h-10 w-1/2 items-center rounded-xl border border-alba-gray-300 bg-alba-gray-100 px-4 pr-10 text-sm text-alba-gray-600'>
                    <span className='truncate'>
                      {resolveQuestionnaireDisplay(
                        index,
                        location.questionnaireAnswers?.[index]
                      )}
                    </span>
                    <ChevronDown
                      className='absolute right-3 h-4 w-4 text-alba-gray-500'
                      strokeWidth={1.6}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}
